// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek
import * as React from "react";
import { connect } from "react-redux";
import { Outlet, RouteObject } from "react-router-dom";
import { RoutedProps, withRouteRouter } from "view/reactrouterinterceptor";

import { MainPanel } from "types";
import { isLocalhost } from "guiutils";
import { isSet } from "utils";
import { IState } from "states/states";
import { IAction, raiseAction, ActionType } from "actions";
import { AppState, UserState } from "states/schedulerstates";
import { UserRole } from "graphql/api";

import ConfirmModal from "view/comp/ConfirmModal";
import Keyboard from "view/comp/Keyboard";
import Navigation from "./Navigation";
import Loading from "view/page/Loading";
import Login from "view/page/Login";
import SignUp from "view/page/SignUp";
import User from "view/page/User";
import UserAdmin from "view/page/UserAdmin";
import UserPwdChange from "view/comp/UserPwdChange";
import UserPwdForgot from "view/comp/UserPwdForgot";
import Site from "view/page/Site";
import SiteAdmin from "view/page/SiteAdmin";
import Dashboard from "view/page/Dashboard";
import IOs from "view/page/IOs";
import IoTrend from "view/page/IoTrend";
import Power from "view/page/Power";
import EnergyMeterList from "view/comp/EnergyMeterList";
import Alarm from "view/page/Alarm";
import Monitor from "view/page/Monitor";
import Trend from "view/page/Trend";
import TrendMonitor from "view/page/TrendMonitor";
import ChargerPage from "view/page/ChargerPage";
import RfidPage from "view/page/RfidPage";
import EnlineChargers from "view/page/EnlineChargers";
import OcppConfiguration from "view/page/OcppConfiguration";
import ChargingSession from "view/page/ChargingSession";
import ChargingSessionList from "view/page/ChargingSessionList";
import Reports from "view/page/Reports";
import LocalSettings from "view/page/LocalSettings";
import ReportDataView from "view/page/ReportDataView";
import ModulesConditions from "view/page/ModulesConditions";
import CustomDashboard from "view/page/CustomDashboard";
import DevTest from "view/page/DevTest";

import SiteAnimation from "view/page/SiteAnimation";

// apply style definitions
import "content.scss";

// uses webpack definitions
declare const IS_CLOUD: boolean;

interface IRootProps {
  hasTouchKeyboard: boolean;
  showLoading: boolean;
  showAnimation: boolean;
  showLoginSignUp: boolean;
  showSiteSelection: boolean;
  wantsSignUp: boolean;
  doesPasswordChange: boolean;
  isInitDone: boolean;
  isRfidUser: boolean;
  siteTitle: string | undefined;
  panel: MainPanel | undefined;
  shouldChangePassword: boolean;
  hasForgotPassword: boolean;
}

const mapStateToProps = (state: IState) => {
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);

  const site = state.site.siteList?.find((curItem) => curItem.id === state.cached.selectedSite);
  const siteTitle = IS_CLOUD
    ? isSet(site)
      ? `enline cloud - ${site.name}`
      : "enline cloud"
    : undefined;

  const showAnimation =
    [
      AppState.INIT,
      AppState.LOGGED_IN,
      AppState.SITE_SELECTED,
      AppState.PARAMETER_LOADED,
      AppState.LIVE_DATA_SUBSCRIBED,
    ].includes(state.scheduler.appState) ||
    (state.scheduler.appState === AppState.SITE_LIST_LOADED && isSet(site));

  const isAdminUser = [UserRole.admin, UserRole.root].includes(curUser?.role ?? UserRole.viewer);

  const showLoginSignUp = showAnimation || state.scheduler.appState === AppState.LOGGED_OUT;

  const props: IRootProps = {
    hasTouchKeyboard: !IS_CLOUD && isLocalhost(),
    showLoading: state.scheduler.appState === AppState.INIT,
    showAnimation,
    showLoginSignUp,
    showSiteSelection:
      !showLoginSignUp && state.scheduler.appState < AppState.SITE_SELECTED && !isAdminUser,
    wantsSignUp:
      state.scheduler.userState === UserState.SINGING_UP ||
      state.scheduler.userState === UserState.SINGING_UP_CONFIRM,
    doesPasswordChange:
      state.router.panel === MainPanel.USER &&
      (state.scheduler.userState === UserState.CHANGING_PASSWORD ||
        (state.scheduler.userState === UserState.UNKNOWN &&
          state.scheduler.appState >= AppState.LOGGED_IN)),
    isInitDone: state.scheduler.appState >= AppState.READY,
    isRfidUser: !!state.site.user?.isRfidUser,
    siteTitle,
    panel: state.router.panel,
    shouldChangePassword: state.scheduler.userState === UserState.CHANGING_PASSWORD,
    hasForgotPassword:
      state.scheduler.userState === UserState.FORGOT_PASSWORD ||
      state.scheduler.userState === UserState.PASSWORD_CONFIRM,
  };
  return props;
};

interface IRootDispatch {
  onVisibilityChange: () => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IRootDispatch = {
    onVisibilityChange: () => {
      const isVisible = document.visibilityState === "visible";
      dispatch(raiseAction(ActionType.ACT_VISIBILITY_CHANGE, isVisible));
    },
  };
  return dispatchProps;
};

export const childRoutes: RouteObject[] = [
  { index: true, element: <Dashboard /> },
  { path: MainPanel.DASHBOARD, element: <Dashboard /> },
  { path: MainPanel.MONITOR, element: <Monitor /> },
  { path: MainPanel.USER, element: <User /> },
  { path: MainPanel.USER_SETTINGS, element: <UserAdmin /> },
  { path: MainPanel.SITES, element: <Site /> },
  { path: MainPanel.SITE_SETTINGS, element: <SiteAdmin /> },
  { path: MainPanel.MONITOR, element: <Monitor /> },
  { path: MainPanel.POWER, element: <Power /> },
  { path: MainPanel.ENERGYMETERS, element: <EnergyMeterList /> },
  { path: MainPanel.ENERGY, element: <Trend /> },
  { path: MainPanel.TRENDMONITOR, element: <TrendMonitor /> },
  { path: MainPanel.REPORT, element: <Reports isReadOnly={true} /> },
  { path: MainPanel.REPORTDATAVIEW, element: <ReportDataView /> },
  { path: MainPanel.IOS, element: <IOs /> },
  { path: MainPanel.IO_TREND, element: <IoTrend /> },
  { path: MainPanel.CHARGERLIST, element: <ChargerPage /> },
  { path: MainPanel.CHARGERDETAIL, element: <ChargerPage /> },
  { path: MainPanel.RFIDLIST, element: <RfidPage /> },
  { path: MainPanel.RFIDDETAIL, element: <RfidPage /> },
  { path: MainPanel.ENLINE_CHARGER, element: <EnlineChargers /> },
  { path: MainPanel.OCPPCONFIGURATION, element: <OcppConfiguration /> },
  { path: MainPanel.CHARGINGSESSIONLIST, element: <ChargingSessionList /> },
  { path: MainPanel.CHARGINGSESSION, element: <ChargingSession /> },
  { path: MainPanel.ALARMS, element: <Alarm /> },
  { path: MainPanel.MODULES, element: <ModulesConditions /> },
  { path: MainPanel.LOCAL_SETTINGS, element: <LocalSettings /> },
  { path: MainPanel.CUSTOM_DASHBOARD, element: <CustomDashboard isTopLevel={true} /> },
  { path: MainPanel.PUBLIC_DISPLAY, element: <CustomDashboard isTopLevel={false} /> },
  { path: MainPanel.DEV_TEST, element: <DevTest /> },
];

class RootComponent extends React.Component<IRootProps & RoutedProps & IRootDispatch> {
  public componentDidMount() {
    document.addEventListener("visibilitychange", this.props.onVisibilityChange);
  }
  public componentWillUnmount() {
    document.removeEventListener("visibilitychange", this.props.onVisibilityChange);
  }

  public componentDidUpdate() {
    if (this.props.siteTitle) {
      document.title = this.props.siteTitle;
    }
  }

  public render() {
    // is the code loading / login check over?
    if (this.props.showLoading) {
      return <Loading />;
    }

    // show content or some kind of navigation page?
    // password change has first priority, since this also applies for
    // new users at first login before logged-in
    const isContent = !this.props.doesPasswordChange && this.props.isInitDone;
    const content = isContent ? (
      <Outlet />
    ) : this.props.doesPasswordChange ? (
      <User />
    ) : this.props.panel === MainPanel.USER_SETTINGS ? (
      <UserAdmin />
    ) : this.props.panel === MainPanel.USER || this.props.isRfidUser ? (
      <User />
    ) : (
      <Site />
    );
    // special handling for custom dashboard page, as this shall be shown fullscreen
    const isFullscreen = this.props.panel === MainPanel.CUSTOM_DASHBOARD;
    const hasContainer =
      !isContent ||
      ![MainPanel.PUBLIC_DISPLAY, MainPanel.CUSTOM_DASHBOARD].includes(this.props.panel!);

    return (
      <>
        <header>{!isFullscreen && <Navigation showNavigation={this.props.isInitDone} />}</header>
        <main className={hasContainer ? "container" : undefined}>
          {this.props.showAnimation ? (
            <SiteAnimation />
          ) : this.props.showLoginSignUp ? (
            this.props.wantsSignUp ? (
              <SignUp />
            ) : this.props.shouldChangePassword ? (
              <UserPwdChange />
            ) : this.props.hasForgotPassword ? (
              <UserPwdForgot />
            ) : (
              <Login />
            )
          ) : this.props.doesPasswordChange ? (
            <User />
          ) : this.props.showSiteSelection ? (
            <Site />
          ) : (
            content
          )}
        </main>
        <ConfirmModal />
        {this.props.hasTouchKeyboard && <Keyboard />}
      </>
    );
  }
}

const Root = connect(mapStateToProps, mapDispatchToProps)(withRouteRouter(RootComponent));

export default Root;
